<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ApiClientError } from "../api/client";
import { listSectionTemplates,getSectionTemplate,getSectionRevision,saveSectionTemplate,applySectionTemplate,getSectionOperation,operationHash,
  type SectionCode,type SectionDraft,type SectionTemplate,type SectionOperation,type SaveTemplateRequest,type ApplyTemplateRequest } from "../api/detail-section-templates";
const props=defineProps<{productId:string;code:SectionCode;snapshot:SectionDraft;currentContent:string;disabled:boolean;captureUndo:()=>()=>void;readback:(value:SectionOperation)=>Promise<void>}>();
const emit=defineEmits<{apply:[value:{template:SectionTemplate;revision:string}];saved:[value:SectionOperation];lock:[value:boolean]}>();
const name=ref(""),items=ref<SectionTemplate[]>([]),selectedId=ref("");
const working=ref(false),loading=ref(false),message=ref("");
const label=computed(()=>props.code==="FILE_SPEC"?"传图规范":"包装与售后");
const imported=ref<{template:SectionTemplate;revision:string}|null>(null);
const undo=ref<{restore:()=>void;imported:typeof imported.value}|null>(null);
const normalizedName=computed(()=>name.value.normalize("NFKC").trim());
const sameName=computed(()=>items.value.find(item=>item.name.toLowerCase()===normalizedName.value.toLowerCase()));

type Pending={kind:"SAVE";request:SaveTemplateRequest;hash:string}|{kind:"APPLY";request:ApplyTemplateRequest;hash:string};
const storageKey=`detail-section-operation:${props.productId}:${props.code}`;
const pending=ref<Pending|null>(null);
const storageInvalid=ref(false);
try {
  const stored=sessionStorage.getItem(storageKey);
  if(stored) {
    const value=JSON.parse(stored) as Pending;
    if(!value.request?.id||!value.hash||!["SAVE","APPLY"].includes(value.kind)) throw new Error("invalid operation");
    pending.value=value;
  }
} catch { storageInvalid.value=true;message.value="保存结果记录无法读取，请保留页面联系管理员核对"; }
const selected=computed(()=>items.value.find(item=>item.id===selectedId.value));
const locked=computed(()=>working.value||!!pending.value||storageInvalid.value);
watch(locked,value=>emit("lock",value),{immediate:true});
function store(value:Pending|null) {
  // Persist before POST. A storage failure stops the write rather than losing reconciliation state.
  if(value) sessionStorage.setItem(storageKey,JSON.stringify(value));
  else sessionStorage.removeItem(storageKey);
  pending.value=value;
}
let loadPromise:Promise<void>|null=null;
async function load(){
  if(loadPromise)return loadPromise;
  loading.value=true;
  loadPromise=(async()=>{
    const all:SectionTemplate[]=[];
    for(let page=1;;page++){
      const result=await listSectionTemplates(props.code,page);
      if(result.page!==page)throw new Error("模板列表读取异常，请刷新商品");
      all.push(...result.items);if(!result.hasMore)break;
    }
    items.value=all;
  })();
  try{await loadPromise;}finally{loadPromise=null;loading.value=false;}
}
async function choose(){
  if(!selected.value||props.disabled||locked.value||loading.value)return;
  const choice=selected.value;
  working.value=true;
  try{
    const template=await getSectionTemplate(choice.id);
    if(template.version!==choice.version){await load();message.value="模板已更新，请重新选择";return;}
    const source=await getSectionRevision(props.productId,props.code);
    if(source.content!==props.currentContent){message.value="栏目已被其他操作修改，请重新读取商品";return;}
    undo.value={restore:props.captureUndo(),imported:imported.value};
    imported.value={template,revision:source.revision};emit("apply",imported.value);
    message.value=`已填入“${template.name}”，保存商品后生效`;
  }catch(cause){message.value=cause instanceof Error?cause.message:"选用失败";}
  finally{selectedId.value="";working.value=false;}
}
function undoReplacement(){
  if(!undo.value||props.disabled||locked.value)return;
  undo.value.restore();imported.value=undo.value.imported;undo.value=null;selectedId.value="";
  message.value="已撤销本次替换";
}
async function complete(result:SectionOperation) {
  const operation=pending.value;
  if(!operation||result.id!==operation.request.id||result.productId!==props.productId||result.code!==props.code||result.kind!==operation.kind||result.requestHash!==operation.hash)throw new Error("操作结果与本次请求不一致，请保留页面核对");
  if(result.kind==="APPLY") { await props.readback(result);imported.value=null;undo.value=null;emit("saved",result); }
  if(result.kind==="SAVE")items.value=[result.template,...items.value.filter(item=>item.id!==result.template.id)];
  store(null);
  message.value=result.kind==="SAVE"?`模板“${result.template.name}”已保存，已使用的商品保持原样。`:"当前栏目已独立保存。";
  if(result.kind==="SAVE"&&imported.value?.template.id===result.template.id&&imported.value.template.version!==result.template.version)message.value="模板已更新；保存商品前请重新选用，当前内容已保留。";
}
async function send(value:Pending) {
  working.value=true;message.value="";
  let writeReturned=false;
  try {
    store(value);
    const result=value.kind==="SAVE"?await saveSectionTemplate(value.request):await applySectionTemplate(props.productId,value.request);
    writeReturned=true;
    await complete(result);
  } catch(cause) {
    // A readback error after a successful write must retain the original reconciliation record.
    if(!writeReturned&&cause instanceof ApiClientError&&[400,403,409,422].includes(cause.status))store(null);
    message.value=(cause instanceof Error?cause.message:"保存失败")+(pending.value?"。结果尚未确认，请使用核对结果，勿重复提交。":"");
    throw cause;
  } finally{working.value=false;}
}
async function submit(){
  if(props.disabled||locked.value||loading.value)return;
  const templateName=normalizedName.value;
  if(!templateName||templateName.length>100||!props.snapshot.blocks.length){message.value="请填写1至100字的名称，栏目至少需要一个内容块";return;}
  const existing=sameName.value;
  working.value=true;
  try{
    const request:SaveTemplateRequest={id:crypto.randomUUID(),templateId:existing?.id??crypto.randomUUID(),expectedVersion:existing?.version??0,productId:props.productId,name:templateName,snapshot:JSON.parse(JSON.stringify(props.snapshot))};
    await send({kind:"SAVE",request,hash:await operationHash(request)});
  }catch(cause){if(!message.value)message.value=cause instanceof Error?cause.message:"保存模板失败";}
  finally{working.value=false;}
}
async function saveImported():Promise<boolean> {
  if(pending.value)throw new Error("栏目模板保存结果尚未确认，请先核对结果");
  if(!imported.value)return false;
  if(working.value)throw new Error("模板操作进行中，请稍后保存");
  const request:ApplyTemplateRequest={id:crypto.randomUUID(),templateId:imported.value.template.id,templateVersion:imported.value.template.version,targetRevision:imported.value.revision,snapshot:JSON.parse(JSON.stringify(props.snapshot))};
  await send({kind:"APPLY",request,hash:await operationHash({productId:props.productId,...request})});
  return true;
}
async function verify() {
  if(!pending.value||working.value)return;
  working.value=true;
  try {await complete(await getSectionOperation(pending.value.request.id));}
  catch(cause){message.value=cause instanceof Error?cause.message:"核对失败";}
  finally{working.value=false;}
}
function canLeave():boolean {
  if(locked.value){message.value="模板操作尚未完成，请先核对结果";return false;}
  return !imported.value||window.confirm("当前栏目有尚未保存的模板草稿，确认放弃并离开？");
}
function resetDraft(){if(!pending.value){imported.value=null;undo.value=null;}}
function beforeUnload(event:BeforeUnloadEvent){if(pending.value||imported.value||working.value){event.preventDefault();event.returnValue="";}}
onMounted(()=>{window.addEventListener("beforeunload",beforeUnload);void load().catch(cause=>{if(!message.value)message.value=cause instanceof Error?cause.message:"模板读取失败";});});
onBeforeUnmount(()=>{window.removeEventListener("beforeunload",beforeUnload);emit("lock",false);});
defineExpose({canLeave,saveImported,resetDraft,load,submit,choose,undoReplacement,verify,pending,imported,selectedId,name,message,loading,selected,sameName});
</script>

<template>
  <div
    class="section-template-tools"
    :aria-label="`${label}模板工具`"
  >
    <div class="template-actions">
      <label class="template-picker">{{ label }}
        <select
          v-model="selectedId"
          :aria-label="`${label}模板`"
          :disabled="disabled||locked||loading"
          @change="choose"
        >
          <option value="">{{ loading?'正在读取模板…':'选择模板即填入' }}</option>
          <option
            v-for="item in items"
            :key="item.id"
            :value="item.id"
          >{{ item.name }}</option>
        </select>
      </label>
      <button
        v-if="undo"
        type="button"
        :disabled="disabled||locked"
        @click="undoReplacement"
      >
        撤销替换
      </button>
      <input
        v-model="name"
        :aria-label="`${label}模板名称`"
        placeholder="模板名称"
        maxlength="100"
        :disabled="disabled||locked||loading"
        @keydown.enter.prevent="submit"
      >
      <button
        type="button"
        :disabled="disabled||locked||loading||!normalizedName||!snapshot.blocks.length"
        @click="submit"
      >
        {{ sameName?'更新此模板':'存为模板' }}
      </button>
      <button
        v-if="pending"
        type="button"
        :disabled="working"
        @click="verify"
      >
        核对保存结果
      </button>
    </div>
    <p
      v-if="message"
      role="status"
    >
      {{ message }}
    </p>
  </div>
</template>

<style scoped>
.section-template-tools{padding:8px 0;border-bottom:1px solid #edf0ee}
.template-actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.template-picker{display:flex;align-items:center;gap:8px;font-weight:600;min-width:0}
button,input,select{padding:7px 10px;border:1px solid #d5dedb;border-radius:5px;background:white;max-width:100%}
select{width:220px}input{width:150px}button:disabled{opacity:.5}p{margin:6px 0 0;color:#53675d;font-size:13px}
@media(max-width:600px){.template-picker{width:100%}select{flex:1;min-width:0}}
</style>
